import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getActiveScreenPopup } from "../api/client";

export default function ScreenPopupModal({ screen = "dashboard" }) {
  const [popup, setPopup] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if dismissed in this session
    const dismissedKey = `fastsend_popup_${screen}_dismissed`;
    if (sessionStorage.getItem(dismissedKey)) {
      return;
    }

    getActiveScreenPopup(screen)
      .then((res) => {
        if (res.data?.popup) {
          setPopup(res.data.popup);
        }
      })
      .catch(() => {});
  }, [screen]);

  const handleDismiss = () => {
    const dismissedKey = `fastsend_popup_${screen}_dismissed`;
    sessionStorage.setItem(dismissedKey, "true");
    setDismissed(true);
  };

  if (!popup || dismissed || !popup.isActive) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.72)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        animation: "fadeIn 0.25s ease-out"
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && popup.dismissible) handleDismiss();
      }}
    >
      <div
        style={{
          background: "linear-gradient(145deg, rgba(28, 32, 45, 0.98), rgba(18, 20, 30, 0.98))",
          border: "1px solid rgba(255, 255, 255, 0.12)",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "460px",
          overflow: "hidden",
          boxShadow: "0 24px 48px -12px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255, 255, 255, 0.08)",
          position: "relative",
          animation: "scaleIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)"
        }}
      >
        {/* Close Button */}
        {popup.dismissible && (
          <button
            onClick={handleDismiss}
            aria-label="Close"
            style={{
              position: "absolute",
              top: "14px",
              right: "14px",
              background: "rgba(255, 255, 255, 0.1)",
              border: "none",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              color: "var(--text-secondary)",
              fontSize: "16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              transition: "all 0.15s ease"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
              e.currentTarget.style.color = "var(--text-secondary)";
            }}
          >
            ✕
          </button>
        )}

        {/* Optional Image */}
        {popup.imageUrl && (
          <div style={{ width: "100%", maxHeight: "180px", overflow: "hidden", background: "#0f111a" }}>
            <img
              src={popup.imageUrl}
              alt={popup.title}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
        )}

        <div style={{ padding: "24px", textAlign: "center" }}>
          {/* Badge */}
          {popup.badge && (
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>
              <span
                style={{
                  display: "inline-block",
                  padding: "4px 12px",
                  borderRadius: "20px",
                  fontSize: "11px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  background: "rgba(22, 199, 132, 0.15)",
                  color: "var(--success-hover, #16c784)",
                  border: "1px solid rgba(22, 199, 132, 0.3)"
                }}
              >
                {popup.badge}
              </span>
            </div>
          )}

          {/* Title */}
          <h3
            style={{
              margin: "0 0 10px 0",
              fontSize: "20px",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.3,
              textAlign: "center"
            }}
          >
            {popup.title}
          </h3>

          {/* Message */}
          <p
            style={{
              margin: "0 auto 20px auto",
              fontSize: "14px",
              lineHeight: 1.55,
              color: "var(--text-secondary, #a0aec0)",
              whiteSpace: "pre-line",
              textAlign: "center",
              maxWidth: "420px"
            }}
          >
            {popup.message}
          </p>

          {/* Action Buttons */}
          {(() => {
            const rawAction = popup.actionButtonText?.trim() || "";
            // Hard clamp at 25 characters to prevent layout breakage
            const buttonText = rawAction.length > 25 ? `${rawAction.slice(0, 22)}...` : rawAction;
            return (
              <div style={{ display: "flex", gap: "10px", marginTop: "16px", justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
                {buttonText && popup.actionButtonLink && (
                  popup.actionButtonLink.startsWith("http") ? (
                    <a
                      href={popup.actionButtonLink}
                      target="_blank"
                      rel="noreferrer"
                      onClick={handleDismiss}
                      title={rawAction}
                      style={{
                        flex: popup.dismissible ? "1 1 140px" : "1 1 200px",
                        maxWidth: "240px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        padding: "11px 18px",
                        borderRadius: "8px",
                        background: "var(--primary-color, #2563eb)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "14px",
                        textDecoration: "none",
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)"
                      }}
                    >
                      {buttonText}
                    </a>
                  ) : (
                    <Link
                      to={popup.actionButtonLink}
                      onClick={handleDismiss}
                      title={rawAction}
                      style={{
                        flex: popup.dismissible ? "1 1 140px" : "1 1 200px",
                        maxWidth: "240px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textAlign: "center",
                        padding: "11px 18px",
                        borderRadius: "8px",
                        background: "var(--primary-color, #2563eb)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "14px",
                        textDecoration: "none",
                        boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)"
                      }}
                    >
                      {buttonText}
                    </Link>
                  )
                )}

                {popup.dismissible && (
                  <button
                    onClick={handleDismiss}
                    style={{
                      flex: buttonText ? "0 1 110px" : "1 1 180px",
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      padding: "11px 20px",
                      borderRadius: "8px",
                      background: "rgba(255, 255, 255, 0.08)",
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                      color: "#e2e8f0",
                      fontSize: "14px",
                      fontWeight: 600,
                      cursor: "pointer",
                      textAlign: "center"
                    }}
                  >
                    {buttonText ? "Dismiss" : "Got it"}
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
